<template>
  <section class="p-6 flex flex-col gap-6">
    <!-- Breadcrumb -->
    <nav class="text-gray-600 text-sm">
      <span class="font-semibold cursor-pointer" @click="$router.push('/')">
        Home
      </span>
      <span class="mx-2">/</span>
      <span class="text-gray-800 font-bold">Articles</span>
    </nav>

    <h3 class="font-bold text-2xl">Articles</h3>

    <!-- Search & Add -->
    <div class="flex items-center justify-between w-full mb-4">
      <input
        v-model="search"
        placeholder="Search by Arabic or English title..."
        class="border border-gray-300 rounded-md px-4 py-2 w-1/3"
      />
      <button
        @click="$router.push({ name: 'AddArticle' })"
        class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
      >
        + Add Article
      </button>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="min-w-full border-collapse bg-white rounded-lg shadow">
        <thead>
          <tr class="bg-gray-100 text-left text-sm font-semibold">
            <th class="px-4 py-2">Title</th>
            <th class="px-4 py-2">Slug</th>
            <th class="px-4 py-2">Image</th>
            <th class="px-4 py-2">Sort Order</th>
            <th class="px-4 py-2">Published</th>
            <th class="px-4 py-2">Created At</th>
            <th class="px-4 py-2 text-center">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="article in filteredArticles"
            :key="article.id"
            class="border-t hover:bg-gray-50 text-sm"
          >
            <!-- Titles -->
            <td class="px-4 py-2">
              <div class="font-semibold text-gray-900">
                {{ article.title_ar }}
              </div>
              <div class="text-xs text-gray-500">
                {{ article.title_en }}
              </div>
            </td>

            <!-- Slug -->
            <td class="px-4 py-2">
              <span class="text-gray-700">
                {{ article.slug || "-" }}
              </span>
            </td>

            <td class="px-4 py-2">
              <div v-if="getArticleImage(article)" class="relative">
               <img
  v-if="getArticleImage(article)"
  :src="getArticleImage(article)"
  class="w-12 h-12 object-cover rounded shadow"
  alt="Article image"
/>

              </div>

              <span v-else class="text-gray-400 text-xs italic">
                No image
              </span>
            </td>

            <!-- Sort Order -->
            <td class="px-4 py-2">
              {{ article.sort_order ?? "-" }}
            </td>

            <!-- Published -->
            <td class="px-4 py-2">
              <span
                :class="article.is_published ? 'text-green-600' : 'text-red-600'"
                class="font-semibold"
              >
                {{ article.is_published ? "Yes" : "No" }}
              </span>
            </td>

            <!-- Created At -->
            <td class="px-4 py-2 whitespace-nowrap">
              {{ formatDate(article.created_at) }}
            </td>

            <!-- Actions -->
            <td class="px-4 py-2 text-center whitespace-nowrap">
              <button
                @click="$router.push({ name: 'EditArticle', params: { id: article.id } })"
                class="text-green-600 hover:underline mx-1"
              >
                Edit
              </button>
              <button
                @click="deleteArticle(article.id)"
                class="text-red-600 hover:underline mx-1"
              >
                Delete
              </button>
            </td>
          </tr>

          <tr v-if="filteredArticles.length === 0">
            <td colspan="7" class="text-center py-4 text-gray-500">
              No articles found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import apiClient from "@/apiClient";

/**
 * ⚠️ مهم
 * ده Base URL للصور فقط (من غير /api)
 */

const articles = ref([]);
const search = ref("");

// ===== helpers =====
const FILE_BASE_URL = "http://127.0.0.1:8000";

function getArticleImage(article) {
  if (!article.image_url) return "";
  return `${FILE_BASE_URL}/files/articles/${article.image_url}`;
}


function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString();
}

// ===== API =====
async function fetchArticles() {
  try {
    const res = await apiClient.get("/articles");
    articles.value = res.data?.data?.articles || [];
  } catch (e) {
    console.error(e);
    articles.value = [];
  }
}

// ===== computed =====
const filteredArticles = computed(() => {
  if (!search.value.trim()) return articles.value;

  const q = search.value.toLowerCase();
  return articles.value.filter(
    (a) =>
      String(a.title_ar || "").toLowerCase().includes(q) ||
      String(a.title_en || "").toLowerCase().includes(q)
  );
});

// ===== actions =====
async function deleteArticle(id) {
  if (!confirm("Are you sure you want to delete this article?")) return;

  try {
    await apiClient.delete(`/articles/${id}`);
    alert("Article deleted successfully");
    fetchArticles();
  } catch (e) {
    alert("Failed to delete article");
  }
}

onMounted(fetchArticles);
</script>
